import { useLazyQuery, useMutation, useQuery } from '@apollo/client'
import ActionButton from '@deltek/specification-client/core/Buttons/ActionButton'
import {
    ShowGlobalSnackbarMutation,
    ShowGlobalSnackbarMutationProps,
    ShowGlobalSnackbarRequest,
} from '@deltek/specification-client/core/Configurations/GlobalSnackbar'
import ProductListingLevels from '@deltek/specification-client/core/Constants/productListingLevels'
import { NavPromptMessageProps } from '@deltek/specification-client/core/Forms/NavPrompt'
import NavPromptDialogContainer from '@deltek/specification-client/core/Forms/NavPromptDialogContainer'
import { CurrentUserInfoQuery } from '@deltek/specification-client/core/GraphQL/Queries/currentUserInfo.queries'
import { CurrentUserInfoResponse } from '@deltek/specification-client/core/Models/currentUserInfo'
import {
    AssignedUser,
    ListingDetail,
} from '@deltek/specification-client/core/Models/listing'
import { ListingLevel } from '@deltek/specification-client/core/Models/product'
import { SearchFacetInput } from '@deltek/specification-client/core/Models/searchFacetInput'
import { User } from '@deltek/specification-client/core/Models/user'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { Checkbox, CircularProgress } from '@material-ui/core'
import { Theme, makeStyles, useTheme } from '@material-ui/core/styles'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import CheckBoxIcon from '@material-ui/icons/CheckBox'
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank'
import {
    CopyProductListing,
    DeleteProductListing,
    MoveProductListing,
} from 'api/graphQL/mutations/productListing.mutation'
import {
    GetListingDetail,
    ListingsGetListingProductTypeProperties,
    ProductSearchAutosuggestQuery,
} from 'api/graphQL/queries/productSearch.queries'
import { ProductSearchAutosuggestResponse } from 'api/graphQL/types/product.types'
import clsx from 'clsx'
import { AutoCompleteInput } from 'pages/ProductsPage/AddProductDialog/AutoCompleteInput'
import { ProductTypeAutocompleteOption } from 'pages/ProductsPage/AddProductDialog/ProductTypeAutocompleteOption'
import {
    isDeltekUserorAdmin,
    trimFamilyAndQuotesFromAutosuggestResponse,
} from 'pages/ProductsPage/productsUtils'
import React, {
    ChangeEvent,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react'
import { useHistory } from 'react-router-dom'
import useWindow from 'shared/hooks/useWindow'
import ListingDetailsUserAssignment from './Assignments'
import ConfirmationDialog from './ConfirmationDialog/ConfirmationDialog'
import {
    ApiInput,
    CopyProductForm,
    CopyProductInput,
    CopyProductProps,
    ListingData,
    MoveProductInput,
    MoveProductProps,
} from './CopyDelete/CopyProduct'
import type {
    ListingHeaderVariants,
    ProductListingDetailForm,
} from './ListingDetailsPage'
import { RoleBasedAcessMatrix } from './ListingDetailsUtils'
import ListingHeaderButton from './ListingHeader/ListingHeaderButton'
import ListingHeaderPopover from './ListingHeader/ListingHeaderPopover'
import ListingLevelVariant from './ListingHeaderVariants/ListingLevelVariant'
import ListingStateVariant from './ListingHeaderVariants/ListingStateVariant'
import ListingStatusVariant, {
    ListingStatus,
} from './ListingHeaderVariants/ListingStatusVariant'
import { AddNotesModal } from './ListingNotes/AddNotesModal'
import PDNButton from './ListingNotes/PDNButton'
import { noteReasonLabels, typeAction } from './ListingNotes/PDNConstants'
import { removeTempNote, resetPDNStore } from './ListingNotes/PDNStore'
import { NoteType } from './ListingNotes/PDNTypes'
import usePDNhook from './ListingNotes/hooks/usePDNHook'
const useStyles = makeStyles((theme: Theme) => ({
    header: {
        padding: theme.spacing(1.5, 2),
        flex: 1,
    },
    properties: {
        backgroundColor: '#F2F2F2',
        height: '100%',
    },
    headerButtonSpacing: { marginRight: theme.spacing(2.5) },
    headerLabel: {
        ...theme.typography.body1,
        margin: theme.spacing(0, 0, 0.5, 0.5),
        display: 'flex',
    },
    headerButtons: {
        '& button': {
            padding: theme.spacing(1.25, 2.25),
            width: 'auto',
            height: 41,
            backgroundColor: '#5E5F61',
            borderRadius: 100,
            color: theme.palette.common.white,
            ...theme.typography.h6,
            lineHeight: 'inherit',
            '&:hover': {
                backgroundColor: '#5E5F61',
            },
        },
    },
    actionButtonContainer: {
        padding: theme.spacing(0, 0.5, 0.5, 0.5),
        '& button': {
            ...theme.typography.h6,
            padding: theme.spacing(1.25, 2),
            borderRadius: 100,
            height: 41,
        },
        '& button.MuiButton-contained.Mui-disabled ': {
            border: '1px solid #0098D6',
            color: '#5E5F61',
        },
    },
    headerButtonContainer: {
        flex: 1,
        flexWrap: 'nowrap',
        '& >div.MuiGrid-item': { margin: '0px 12px' },
    },
    mfgUpdateButton: {
        marginTop: theme.spacing(3.5),
    },

    editPageButtonContainer: {
        '&>span': {
            ...theme.typography.body1,
            marginBottom: theme.spacing(0.5),
            display: 'flex',
        },
        '& button': {
            backgroundColor: '#0098D6',
            color: theme.palette.secondary.light,
            '&:hover': {
                backgroundColor: theme.palette.primary,
            },
        },
    },
    dangerButtons: {
        '&>span': {
            ...theme.typography.body1,
            marginBottom: theme.spacing(0.5),
            display: 'flex',
        },
        '& button': {
            backgroundColor: '#d3455b',
            color: theme.palette.secondary.light,
            '&:hover': {
                backgroundColor: '#e9a2ad',
            },
        },
    },
    rowContainer: {
        backgroundColor: theme.palette.secondary.light,
    },
    cancelButtonContainer: {
        '& button': {
            border: `1px solid ${theme.palette.primary.main}`,
        },
    },
    savePublishMarginTop: {
        '& button': {
            marginTop: theme.spacing(3.5),
        },
    },
    headerButtonWrapper: {
        '@media (min-width: 1280px)': {
            flex: 4,
        },
    },
    addProductButtonContainer: {
        '&>span': {
            ...theme.typography.body1,
            marginBottom: theme.spacing(0.5),
            display: 'flex',
        },
        '& button': {
            backgroundColor: '#33B55B',
            color: theme.palette.secondary.light,
            '&:hover': {
                backgroundColor: '#268e46',
            },
        },
    },
    optionalButtonContainer: {
        '& > div:last-child': {
            marginRight: pxToRem(15),
        },
        '@media (min-width: 1164px) and (max-width: 1280px)': {
            flexDirection: 'column',
            alignItems: 'flex-end',
            '& > div:not(:first-child) > div': {
                marginTop: pxToRem(10),
            },
        },

        flexDirection: 'row',
        alignItems: 'flex-end',
        marginBottom: theme.spacing(1),
    },
}))
export interface ListingHeaderProps {
    assignment?: string
    firmId?: string
    editMode: boolean
    listingLevel?: ListingLevel
    isActive?: boolean
    isAddProductActive?: boolean
    onAddProductClick: VoidFunction
    onCancelClick: VoidFunction
    onEditClick: VoidFunction
    onPublishClick: VoidFunction
    onSaveClick: VoidFunction
    onHeaderChanges: (
        field: ListingHeaderVariants,
        value: string | boolean
    ) => void
    submissionStatus?: string
    saveProductListingDetailLoading: boolean
    disableSave?: boolean
    assignedBPM?: Partial<AssignedUser>[] | null
    assignedDeltek?: Partial<AssignedUser>[] | null
    allBPMUsers: Partial<User>[]
    allDeltekUsers: Partial<User>[]
    productInfo?: Partial<ListingDetail>
    handleBPMUsersChange?: (
        /** Event source */
        users: Partial<AssignedUser>[],
        /** New selection */
        valid: boolean
    ) => void
    handleDeltekUsersChange?: (
        /** Event source */
        users: Partial<AssignedUser>[],
        /** New selection */
        valid: boolean
    ) => void
    roleBasedAccess: Partial<RoleBasedAcessMatrix>
    isDeprecated: boolean
    productListingDetailFormProp: ProductListingDetailForm
}

const noChange = 'No Change'

const blankForm = {
    sectionNames: { value: '', valid: true },
    productType: { value: '', valid: true },
    isActive: { value: 'Active', valid: true },
    listingType: { value: noChange, valid: true },
    state: { value: noChange, valid: true },
    includeManufacturerOptions: { value: 'true', valid: true },
}

export const ListingHeader: React.FC<ListingHeaderProps> = ({
    isAddProductActive,
    assignment,
    editMode,
    listingLevel,
    isActive,
    onAddProductClick,
    onCancelClick,
    onEditClick,
    onPublishClick,
    onSaveClick,
    onHeaderChanges,
    submissionStatus,
    saveProductListingDetailLoading,
    disableSave,
    assignedBPM,
    assignedDeltek,
    allBPMUsers,
    productInfo,
    allDeltekUsers,
    handleBPMUsersChange,
    handleDeltekUsersChange,
    roleBasedAccess,
    isDeprecated,
    productListingDetailFormProp,
}) => {
    const history = useHistory()
    const classes = useStyles()
    const theme = useTheme()
    const { handleSaveNote, isPDNModalOpen, setIsPDNModalOpen } = usePDNhook()

    const { getWindowDomain, getWindowQuery, getWindowURL } = useWindow()

    const assignedBPMLabel =
        assignedBPM && assignedBPM?.length > 0
            ? assignedBPM.length === 1
                ? `${assignedBPM[0]?.profileInfo?.firstName} ${assignedBPM[0]?.profileInfo?.lastName}`
                : `${assignedBPM[0]?.profileInfo?.firstName} ${
                      assignedBPM[0]?.profileInfo?.lastName
                  } (${assignedBPM.length - 1} more...)`
            : assignment
    const assignedDeltekLabel =
        assignedDeltek && assignedDeltek?.length > 0
            ? assignedDeltek.length === 1
                ? `${assignedDeltek[0]?.profileInfo?.firstName} ${assignedDeltek[0]?.profileInfo?.lastName}`
                : `${assignedDeltek[0]?.profileInfo?.firstName} ${
                      assignedDeltek[0]?.profileInfo?.lastName
                  } (${assignedDeltek.length - 1} more...)`
            : assignment

    const [tempPDNStore, setTempPDNStore] = useState<{
        actionType: string | keyof typeof noteReasonLabels
        value: [ListingHeaderVariants | string, string | boolean] | never[]
    }>({
        actionType: '',
        value: [],
    })

    const [
        popoverAnchorEl,
        setPopoverAnchorEl,
    ] = React.useState<HTMLButtonElement | null>(null)
    const [popoverVariant, setpopoverVariant] = React.useState<string | null>(
        null
    )
    const [copyOrMoveModalOpen, setCopyOrMoveModalOpen] = React.useState<
        boolean
    >(false)
    const [
        productListingDetailForm,
        setProductListingDetailForm,
    ] = React.useState<Partial<CopyProductForm>>({ ...blankForm })

    const [sourceProductCount, setSourceProductCount] = React.useState(0)
    const [matchPropertiesCount, setMatchPropertiesCount] = React.useState(0)
    const currentAutoSuggest = React.useRef<string>('')

    const [autoSuggestResults, setAutoSuggestResults] = React.useState<
        Record<string, string[]>
    >({})

    const [getListingDetail, { data: listingDetailData = {} }] = useLazyQuery<
        any
    >(GetListingDetail, {
        displayName: 'ProductListingDetailResult',
        fetchPolicy: 'no-cache',
        onCompleted: () => {},
        onError: (error) => {
            showGlobalSnackbar({
                variables: {
                    message: error?.message || 'Error on fetching data',
                    variant: 'error',
                    id: 'Product_listing_detail_result_error',
                },
            })
        },
    })

    const [
        getDestListingDetail,
        { data: destListingDetailData = {} },
    ] = useLazyQuery<any>(ListingsGetListingProductTypeProperties, {
        displayName: 'ListingProductTypePropertiesResult',
        fetchPolicy: 'no-cache',
        onCompleted: () => {
            let totalMatched = 0
            let values =
                productListingDetailFormProp?.properties?.normalizedProperties
            let newValues = Object.keys(values)
                .map((propertyName) => {
                    return values[propertyName]
                        .filter(({ optionGroups }) => {
                            let optionArray = optionGroups
                                ?.map(({ options }) =>
                                    options?.filter(
                                        (option) => option.isSupported
                                    )
                                )
                                .flat(1)

                            return optionArray && optionArray.length > 0
                        })
                        .map(({ originalPropertyName, subPropertyName }) => {
                            return (
                                (originalPropertyName?.trim()
                                    ? originalPropertyName + '|'
                                    : '') + subPropertyName
                            ).replaceAll(' / ', '|')
                        })
                })
                .flat(3)

            destListingDetailData?.listingsGetListingProductTypeProperties?.properties?.map(
                ({
                    propertyId: propertyIdDest = '',
                    propertyName: propertyNameDest = '',
                }) => {
                    if (newValues.includes(propertyNameDest)) totalMatched++
                }
            )
            setMatchPropertiesCount(totalMatched)
        },
        onError: (error) => {
            showGlobalSnackbar({
                variables: {
                    message: error?.message || 'Error on fetching data',
                    variant: 'error',
                    id: 'listing_product_type_properties_error',
                },
            })
        },
    })

    const [
        executeAutoSuggestion,
        { loading: autosuggestLoading, data: autosuggestData },
    ] = useLazyQuery<ProductSearchAutosuggestResponse>(
        ProductSearchAutosuggestQuery,
        {
            fetchPolicy: 'no-cache',
            onError: () => {
                showGlobalSnackbar({
                    variables: {
                        message: 'Something went wrong with autosuggest',
                        variant: 'error',
                        id: 'autosuggest_Error',
                    },
                })
            },
        }
    )

    const [copyProduct, { loading: loadingCopyProject }] = useMutation<
        CopyProductProps,
        { input: CopyProductInput }
    >(CopyProductListing, {
        onCompleted: () => {
            resetPDNStore()
            history.push(`/products/all?${getWindowQuery()}`)
        },
        onError: (error) => {
            showGlobalSnackbar({
                variables: {
                    message:
                        error?.message ||
                        'Error on Move project product listing',
                    variant: 'error',
                    id: `ProductListingsPage_ProjectProductListingError`,
                },
            })
        },
    })

    const [moveProduct, { loading: loadingMoveProject }] = useMutation<
        MoveProductProps,
        { input: MoveProductInput }
    >(MoveProductListing, {
        onCompleted: () => {
            resetPDNStore()
            history.push(`/products/all?${getWindowQuery()}`)
        },
        onError: (error) => {
            showGlobalSnackbar({
                variables: {
                    message:
                        error?.message ||
                        'Error on Move project product listing',
                    variant: 'error',
                    id: `ProductListingsPage_ProjectProductListingError`,
                },
            })
        },
    })

    const downMD = useMediaQuery(theme.breakpoints.down('sm'))
    const { data: currentUserInfoResponse } = useQuery<CurrentUserInfoResponse>(
        CurrentUserInfoQuery
    )
    const { add, update, submit } = roleBasedAccess

    const handleListingLevelChange = (value: string) => {
        if (value === productInfo?.type && listingLevel === productInfo?.type) {
            handlePopoverClose()
            return
        }

        if (value === productInfo?.type || !!isAddProductActive) {
            removeTempNote('LISTINGTYPECHANGE')
            onHeaderChanges('listingLevel', value)
            handlePopoverClose()
            return
        }

        setTempPDNStore({
            actionType: 'LISTINGTYPECHANGE',
            value: ['listingLevel', value],
        })

        setIsPDNModalOpen(true)
        handlePopoverClose()
    }

    const handleStatusChange = (value: string) => {
        if (
            (!!productInfo?.isActive && value === 'Active' && !!isActive) ||
            (!productInfo?.isActive && value !== 'Active' && !isActive)
        ) {
            handlePopoverClose()
            return
        }

        if (
            (!!productInfo?.isActive && value === 'Active') ||
            (!productInfo?.isActive && value !== 'Active') ||
            !!isAddProductActive
        ) {
            removeTempNote('DELETEORSTATUSCHANGE')
            onHeaderChanges('status', value === 'Active')
            handlePopoverClose()
            return
        }

        setTempPDNStore({
            actionType: 'STATUSCHANGE',
            value: ['status', value === 'Active'],
        })

        setIsPDNModalOpen(true)
        handlePopoverClose()
    }

    const handleStateChange = (value: string) => {
        onHeaderChanges('state', value)
        handlePopoverClose()
    }

    const {
        isDeltekAdmin,
        isDeltekPublishingAuthor,
        isDeltekAuthor,
    } = isDeltekUserorAdmin(currentUserInfoResponse)

    const isDeltekAdminOrAuthor =
        isDeltekAdmin || isDeltekPublishingAuthor || isDeltekAuthor
    const canEdit = !!(add || update || isDeprecated)

    const handleButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        if (isDeltekAdminOrAuthor) {
            const button = event.currentTarget
            setPopoverAnchorEl(button)
            setpopoverVariant(button.id)
        }
    }

    const handleCancelCreatePDN = useCallback(() => {
        setIsPDNModalOpen?.(false)
        setTempPDNStore({
            actionType: '',
            value: [],
        })
    }, [setIsPDNModalOpen])

    const handlePopoverClose = React.useCallback(() => {
        setPopoverAnchorEl(null)
        setpopoverVariant(null)
    }, [])

    const [showGlobalSnackbar] = useMutation<
        ShowGlobalSnackbarRequest,
        ShowGlobalSnackbarMutationProps
    >(ShowGlobalSnackbarMutation)

    const handleCopyOrMove = React.useCallback((type) => {
        NavPromptDialogContainer(
            JSON.stringify({
                title: '',
                preferredMessage: `You are about to ${type} a project are you sure you want to continue?`,
                buttonColor: '#d3455b',
                confirmText: 'Okay',
                cancelText: 'Cancel',
            } as NavPromptMessageProps),
            (callbackState: boolean) => {
                if (!callbackState) return

                setTempPDNStore({
                    actionType: type === 'Move' ? 'MOVE' : 'COPY',
                    value: [],
                })

                setIsPDNModalOpen(true)
            }
        )
    }, [])

    const callAutoSuggestQuery = React.useCallback(
        (
            searchField: string,
            value: string,
            validateFamily = false,
            validateProductTypeName = false
        ) => {
            if (
                searchField === 'sectionNames' ||
                searchField === 'productType'
            ) {
                currentAutoSuggest.current =
                    validateFamily || validateProductTypeName
                        ? validateFamily
                            ? 'validateFamily'
                            : 'validateProductTypeName'
                        : searchField
                const autoPayload = {
                    input: {
                        searchLimit: 50,
                        facets: [] as Partial<SearchFacetInput>[],
                        searchField:
                            searchField !== 'productType'
                                ? searchField
                                : 'sectionNameWithProductTypeWithPremiumPlusWithPtid',
                        searchText: value ?? '',
                    },
                }
                if (searchField === 'productType')
                    autoPayload.input.facets = [
                        {
                            name: 'listingLevels',
                            values: [''],
                        },
                        {
                            name: 'sectionNames',
                            values: [
                                productListingDetailForm.sectionNames?.value ||
                                    '',
                            ],
                        },
                    ]
                executeAutoSuggestion({
                    variables: autoPayload,
                })
            }
        },
        [executeAutoSuggestion, currentAutoSuggest, productListingDetailForm]
    )

    const handleInputChange = React.useCallback(
        (event: ChangeEvent<{}>, val: string) => {
            const targetInput = event?.currentTarget as HTMLInputElement
            const searchField = targetInput?.name

            callAutoSuggestQuery(searchField || 'productType', val)
        },
        [callAutoSuggestQuery]
    )

    const handleOpen = React.useCallback(
        (searchField = '') => {
            callAutoSuggestQuery(searchField, '')
        },
        [callAutoSuggestQuery, productListingDetailForm]
    )

    const validateNewFamily = React.useCallback(
        (key: string, value: string) => {
            let sectionNames = ''
            let isValid = true
            if (key === 'sectionNumber') {
                const familyNumber = value?.split('.')
                isValid =
                    familyNumber[0]?.trim()?.length === 6 &&
                    !isNaN(+familyNumber[0]) &&
                    (!familyNumber[1] ||
                        (familyNumber[1]?.trim()?.length <= 2 &&
                            !isNaN(+familyNumber[1])))
            }
            callAutoSuggestQuery('sectionNames', sectionNames, true)
            return isValid
        },
        [callAutoSuggestQuery, productListingDetailForm]
    )

    const columnValueChangeHandler = React.useCallback(
        (key: string, value: string | string[]) => {
            const isNonEmpty = !!(
                value &&
                ((typeof value === 'string' && value.trim()) ||
                    (typeof value === 'object' && value.length > 0))
            )
            const prevFormState = { ...productListingDetailForm }

            const isNewFamilyValid =
                key === 'sectionNumber' &&
                validateNewFamily(key, value as string)

            const isvalid =
                key === 'sectionNumber' ? isNewFamilyValid : isNonEmpty

            setProductListingDetailForm({
                ...(key === 'sectionNames'
                    ? {
                          ...prevFormState,
                          productType: { value: '', valid: true },
                      }
                    : prevFormState),
                [key]: {
                    value:
                        key === 'sectionNames'
                            ? (value as string).trim()
                            : value,
                    valid: isvalid,
                },
            })
        },
        [
            productListingDetailForm,
            callAutoSuggestQuery,
            validateNewFamily,
            setProductListingDetailForm,
        ]
    )

    const handleProductTypeChange = React.useCallback(
        (_: ChangeEvent<{}>, value: string | null) => {
            const delimitedOption = value?.split('%|%')
            const newVal =
                delimitedOption && delimitedOption?.length > 0
                    ? delimitedOption[0]
                    : ''
            columnValueChangeHandler('productType', newVal ?? '')
        },
        [columnValueChangeHandler]
    )

    const handleAutocompleteChange = React.useCallback(
        (key: string) => (_: ChangeEvent<{}>, value: string | null) => {
            columnValueChangeHandler(key, value ?? '')
        },
        [columnValueChangeHandler]
    )

    const handleAutocompleteOpen = React.useCallback(
        (searchField: string) => (_: ChangeEvent<{}>) => {
            handleOpen?.(searchField)
        },
        [handleInputChange]
    )

    const handleIncludeManufacturerOptions = useCallback(() => {
        columnValueChangeHandler(
            'includeManufacturerOptions',
            `${
                productListingDetailForm.includeManufacturerOptions?.value ===
                'true'
                    ? 'false'
                    : 'true'
            }`
        )
    }, [
        columnValueChangeHandler,
        productListingDetailForm.includeManufacturerOptions,
    ])

    const listingTypeItems = useMemo(() => {
        const productListingLevels = ProductListingLevels.filter(
            (level) => level !== productInfo?.type
        )
        return [noChange, ...productListingLevels]
    }, [productInfo?.type])

    const stateItems = useMemo(() => {
        const { listingType: newListingType } = productListingDetailForm
        const defaultOptions = [noChange, ListingStatus.Completed]
        const listingType =
            newListingType?.value === noChange
                ? productInfo?.type
                : newListingType?.value || ''
        switch (listingType) {
            case ListingLevel.Premium: {
                defaultOptions.splice(
                    1,
                    0,
                    ...[
                        ListingStatus.PremiumReview,
                        ListingStatus.PremiumReviewBPMSubmitted,
                    ]
                )
                break
            }
            case ListingLevel.PremiumMasterSpec: {
                defaultOptions.splice(
                    1,
                    0,
                    ...[
                        ListingStatus.PremiumMasterSpecReview,
                        ListingStatus.PremiumMasterSpecReviewBPMSubmitted,
                    ]
                )
                break
            }
            case ListingLevel.PremiumProprietary: {
                defaultOptions.splice(
                    1,
                    0,
                    ...[
                        ListingStatus.ProprietaryReview,
                        ListingStatus.ProprietaryReviewBPMSubmitted,
                    ]
                )
                break
            }
            case ListingLevel.Manufacturer: {
                if (
                    productInfo?.type !== ListingLevel.Manufacturer &&
                    productInfo?.submissionStatus !== ListingStatus.Completed
                )
                    defaultOptions.splice(0, 1)
            }
            default:
                defaultOptions
        }
        const stateOptions = defaultOptions.filter(
            (state) => state !== productInfo?.submissionStatus
        )
        const prevFormState = { ...productListingDetailForm }
        setProductListingDetailForm({
            ...prevFormState,
            state: { value: stateOptions[0], valid: true },
        })

        return stateOptions
    }, [productInfo?.type, productListingDetailForm.listingType])

    const confirmationDialogContent = React.useMemo(() => {
        // need to fix this
        const productInfotemp: any = productInfo
        return ListingData.map(
            ({ name, key }: { name: string; key: string }, index: number) => (
                <GridContainer
                    style={{ marginBottom: pxToRem(20) }}
                    key={`${index}-${key}`}
                >
                    <GridItem
                        style={{
                            width: pxToRem(160),
                            fontSize: '1.5rem',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            display: 'flex',
                            marginRight: '10px',
                            textAlign: 'end',
                        }}
                    >
                        {name}:
                    </GridItem>
                    <GridItem
                        style={{
                            maxWidth: pxToRem(500),
                            width: '100%',
                            wordBreak: 'break-all',
                        }}
                    >
                        {key === 'family' ? (
                            <GridItem
                                className={clsx(
                                    classes.rowContainer,
                                    'rowContainer'
                                )}
                                xs={12}
                                style={{ width: '100%' }}
                                data-testid="sectionNames"
                            >
                                <AutoCompleteInput
                                    name="sectionNames"
                                    label=""
                                    valid={
                                        productListingDetailForm.sectionNames
                                            ?.valid
                                    }
                                    handleOpen={handleAutocompleteOpen(
                                        'sectionNames'
                                    )}
                                    handleChange={handleAutocompleteChange(
                                        'sectionNames'
                                    )}
                                    handleInputChange={handleInputChange}
                                    selectedValue={
                                        (productListingDetailForm.sectionNames
                                            ?.value ?? '') as string
                                    }
                                    loading={autosuggestLoading}
                                    items={
                                        autoSuggestResults.sectionNames ?? []
                                    }
                                    disabled={
                                        loadingCopyProject || loadingMoveProject
                                    }
                                />
                            </GridItem>
                        ) : key === 'newProductType' ? (
                            <GridItem
                                style={{ width: '100%' }}
                                className={clsx(
                                    classes.rowContainer,
                                    'rowContainer'
                                )}
                                data-testid="productType"
                                xs={12}
                            >
                                <AutoCompleteInput
                                    name="productType"
                                    label=""
                                    disabled={
                                        !productListingDetailForm.sectionNames
                                            ?.value ||
                                        loadingCopyProject ||
                                        loadingMoveProject
                                    }
                                    valid={
                                        productListingDetailForm.productType
                                            ?.valid
                                    }
                                    handleOpen={handleAutocompleteOpen(
                                        'productType'
                                    )}
                                    handleChange={handleProductTypeChange}
                                    handleInputChange={handleInputChange}
                                    selectedValue={
                                        (productListingDetailForm.productType
                                            ?.value ?? '') as string
                                    }
                                    loading={autosuggestLoading}
                                    items={autoSuggestResults.productType ?? []}
                                    customOptions={(option: string) => (
                                        <ProductTypeAutocompleteOption
                                            option={option}
                                        />
                                    )}
                                />
                            </GridItem>
                        ) : key === 'newIsActive' ? (
                            <GridItem
                                style={{ width: '100%' }}
                                className={clsx(
                                    classes.rowContainer,
                                    'rowContainer'
                                )}
                                data-testid="productType"
                                xs={12}
                            >
                                <AutoCompleteInput
                                    name="isActive"
                                    label=""
                                    disabled={
                                        loadingCopyProject || loadingMoveProject
                                    }
                                    valid={true}
                                    handleOpen={() => handleOpen?.('isActive')}
                                    handleChange={(
                                        _e: ChangeEvent<{}>,
                                        value: string | null
                                    ) =>
                                        columnValueChangeHandler(
                                            'isActive',
                                            value ?? ''
                                        )
                                    }
                                    handleInputChange={handleInputChange}
                                    selectedValue={
                                        (productListingDetailForm.isActive
                                            ?.value ?? '') as string
                                    }
                                    items={['Active', 'Inactive']}
                                />
                            </GridItem>
                        ) : key === 'isActive' ? (
                            productInfo?.isActive ? (
                                'Active'
                            ) : (
                                'Inactive'
                            )
                        ) : key === 'categories' ? (
                            productInfotemp?.categories?.join(', ') || ''
                        ) : key === 'includeManufacturerOptions' ? (
                            <GridItem
                                style={{ width: '100%' }}
                                className={clsx(
                                    classes.rowContainer,
                                    'rowContainer'
                                )}
                                data-testid="includeManufacturerOptions"
                            >
                                <Checkbox
                                    checked={
                                        productListingDetailForm
                                            .includeManufacturerOptions
                                            ?.value === 'true'
                                    }
                                    color="default"
                                    icon={
                                        <CheckBoxOutlineBlankIcon fontSize="small" />
                                    }
                                    checkedIcon={
                                        <CheckBoxIcon fontSize="small" />
                                    }
                                    name="includeManufacturerOptions"
                                    onClick={handleIncludeManufacturerOptions}
                                />
                            </GridItem>
                        ) : key === 'newListingType' ? (
                            <GridItem
                                style={{ width: '100%' }}
                                className={clsx(
                                    classes.rowContainer,
                                    'rowContainer'
                                )}
                                data-testid="listingType"
                            >
                                <AutoCompleteInput
                                    name="listingType"
                                    label=""
                                    disabled={
                                        loadingCopyProject || loadingMoveProject
                                    }
                                    valid={
                                        productListingDetailForm.listingType
                                            ?.valid
                                    }
                                    handleOpen={handleAutocompleteOpen(
                                        'listingType'
                                    )}
                                    handleChange={handleAutocompleteChange(
                                        'listingType'
                                    )}
                                    handleInputChange={handleInputChange}
                                    selectedValue={
                                        (productListingDetailForm.listingType
                                            ?.value ?? '') as string
                                    }
                                    loading={autosuggestLoading}
                                    items={listingTypeItems ?? []}
                                />
                            </GridItem>
                        ) : key === 'newState' ? (
                            <GridItem
                                style={{ width: '100%' }}
                                className={clsx(
                                    classes.rowContainer,
                                    'rowContainer'
                                )}
                                data-testid="state"
                            >
                                <AutoCompleteInput
                                    name="state"
                                    label=""
                                    disabled={
                                        loadingCopyProject || loadingMoveProject
                                    }
                                    valid={
                                        productListingDetailForm.state?.valid
                                    }
                                    handleOpen={handleAutocompleteOpen('state')}
                                    handleChange={handleAutocompleteChange(
                                        'state'
                                    )}
                                    handleInputChange={handleInputChange}
                                    selectedValue={
                                        (productListingDetailForm.state
                                            ?.value ?? '') as string
                                    }
                                    loading={autosuggestLoading}
                                    items={stateItems ?? []}
                                />
                            </GridItem>
                        ) : (
                            productInfotemp?.[key]
                        )}
                    </GridItem>
                </GridContainer>
            )
        )
    }, [
        autosuggestData,
        autosuggestLoading,
        autoSuggestResults,
        productListingDetailForm,
        productInfo,
    ])

    const handleCopyModalOpen = React.useCallback(
        () => setCopyOrMoveModalOpen(true),
        []
    )
    const handleCopyModalClose = React.useCallback(
        () => setCopyOrMoveModalOpen(false),
        []
    )
    const [
        deleteProjectSearches,
        { loading: loadingDeleteSearches },
    ] = useMutation<any, { input: any }>(DeleteProductListing, {
        onCompleted: () => {
            resetPDNStore()
            history.push(`/products/all?${getWindowQuery()}`)
        },
    })

    const handleDelete = React.useCallback(() => {
        NavPromptDialogContainer(
            JSON.stringify({
                title: 'Confirm Delete?',
                preferredMessage:
                    'You have chosen to delete this product, Continue:',
                buttonColor: '#d3455b',
                confirmText: 'Yes',
                cancelText: 'No',
            } as NavPromptMessageProps),
            (callbackState: boolean) => {
                if (!callbackState) return
                setTempPDNStore({ actionType: 'DELETE', value: [] })
                setIsPDNModalOpen(true)
            }
        )
    }, [productInfo])

    const handleSavePDN = useCallback(
        ({ noteType, longDescription, noteId, reason }: any) => {
            if (
                ['STATUSCHANGE', 'LISTINGTYPECHANGE'].includes(
                    tempPDNStore.actionType
                )
            ) {
                handleSaveNote?.({
                    noteType,
                    longDescription,
                    isEdit: false,
                    isTemp: true,
                    noteId,
                    reason,
                })
                onHeaderChanges(
                    tempPDNStore.value[0] as ListingHeaderVariants,
                    tempPDNStore.value[1]
                )
                return
            }

            if (tempPDNStore.actionType === 'DELETE') {
                deleteProjectSearches({
                    variables: {
                        input: {
                            listingId: productInfo?.listingId,
                            productDetailNote: {
                                listingId: productInfo?.listingId ?? '',
                                content: longDescription,
                                productDetailNoteType: noteType,
                                reason,
                                origin: getWindowDomain(),
                            },
                        },
                    },
                })
            }

            if (noteType === 'COPYMOVE') {
                const productDetail = productListingDetailForm.sectionNames?.value?.split(
                    ' - '
                )

                const apiInput: ApiInput = {
                    listingId: productInfo?.listingId || '',
                    productType:
                        productListingDetailForm.productType?.value || '',
                    sectionNumber: productDetail?.[0] || '',
                    sectionTitle: productDetail?.[1] || '',
                    isActive:
                        productListingDetailForm.isActive?.value === 'Active',
                    type:
                        productListingDetailForm.listingType?.value === noChange
                            ? productInfo?.type || ''
                            : productListingDetailForm.listingType?.value || '',
                    productName: productInfo?.productName || '',
                    productDetailNote: {
                        listingId: productInfo?.listingId ?? '',
                        content: longDescription,
                        productDetailNoteType: noteType,
                        reason,
                        origin: getWindowDomain(),
                    },
                    newState:
                        productListingDetailForm.state?.value === noChange
                            ? productInfo?.submissionStatus || ''
                            : productListingDetailForm.state?.value || '',
                    includeManufacturerOptions:
                        productListingDetailForm.includeManufacturerOptions
                            ?.value === 'true',
                }

                if (tempPDNStore.actionType === 'MOVE') {
                    moveProduct({
                        variables: {
                            input: apiInput,
                        },
                    })
                } else {
                    copyProduct({
                        variables: {
                            input: apiInput,
                        },
                    })
                }
            }
        },
        [
            handleSaveNote,
            tempPDNStore,
            deleteProjectSearches,
            onHeaderChanges,
            productInfo?.listingId,
            productInfo?.type,
            productInfo?.productName,
            copyProduct,
            moveProduct,
            productListingDetailForm.sectionNames?.value,
            productListingDetailForm.isActive?.value,
        ]
    )

    const handleResetAddNote = useCallback(() => {
        setIsPDNModalOpen(false)
    }, [])

    useEffect(() => {
        if (productInfo?.listingId) {
            getListingDetail({
                variables: {
                    input: {
                        listingId: productInfo?.listingId || '',
                        logEventSource: 'CopyMoveListing',
                    },
                },
            })
        }
    }, [productInfo?.listingId])

    useEffect(() => {
        if (productListingDetailForm.productType?.value) {
            getDestListingDetail({
                variables: {
                    input: {
                        productType:
                            productListingDetailForm.productType?.value,
                        sectionName:
                            productListingDetailForm.sectionNames?.value,
                    },
                },
            })
        }
    }, [productListingDetailForm])

    useEffect(() => {
        if (productListingDetailFormProp?.properties?.normalizedProperties) {
            setSourceProductCount(
                Object.keys(
                    productListingDetailFormProp?.properties
                        ?.normalizedProperties
                )
                    .map((propertyName) => {
                        return productListingDetailFormProp?.properties
                            ?.normalizedProperties[propertyName]
                    })
                    .flat(1).length
            )
        }
    }, [productListingDetailFormProp])

    useEffect(() => {
        if (!autosuggestData) return

        if (currentAutoSuggest.current && autosuggestData) {
            switch (currentAutoSuggest.current) {
                case 'productType':
                    setAutoSuggestResults({
                        [currentAutoSuggest.current]:
                            trimFamilyAndQuotesFromAutosuggestResponse(
                                autosuggestData?.listingAutoSuggest.result ?? []
                            ) ?? [],
                    })
                    break
                case 'sectionNames':
                    setAutoSuggestResults({
                        [currentAutoSuggest.current]:
                            autosuggestData?.listingAutoSuggest.result ?? [],
                    })
                    break
                default:
                    setAutoSuggestResults({
                        [currentAutoSuggest.current]: [],
                    })
                    break
            }
        }
    }, [
        autosuggestData,
        showGlobalSnackbar,
        productListingDetailForm,
        listingDetailData,
    ])

    return (
        <GridContainer
            justify="space-between"
            className={classes.header}
            data-testid="listingHeaderContainer"
        >
            <GridItem className={classes.headerButtonWrapper}>
                <GridContainer className={classes.headerButtonContainer}>
                    <GridItem wrap="nowrap">
                        <span className={classes.headerLabel}>
                            LISTING TYPE
                        </span>
                        <GridContainer
                            item
                            data-testid="listingHeaderListingType"
                        >
                            <ListingHeaderButton
                                buttonKey={'listingLevel'}
                                minWidth={150}
                                buttonLabel={listingLevel as string}
                                isPopoverOpen={
                                    popoverVariant === 'listingLevel'
                                }
                                handleButtonClick={handleButtonClick}
                                isOnEditMode={editMode && isDeltekAdminOrAuthor}
                            />
                            <ListingHeaderPopover
                                popoverContents={
                                    <ListingLevelVariant
                                        onChange={handleListingLevelChange}
                                    />
                                }
                                sectionLabel={'Listing Type'}
                                isPopoverOpen={
                                    popoverVariant === 'listingLevel'
                                }
                                popoverAnchorEl={popoverAnchorEl}
                                handlePopoverClose={handlePopoverClose}
                            />
                        </GridContainer>
                    </GridItem>
                    <GridItem wrap="nowrap">
                        <span
                            className={clsx(
                                classes.headerLabel,
                                'state-component'
                            )}
                        >
                            STATE
                        </span>
                        <GridContainer item data-testid="listingHeaderState">
                            <ListingHeaderButton
                                buttonKey={'state'}
                                minWidth={150}
                                buttonLabel={submissionStatus as string}
                                isPopoverOpen={popoverVariant === 'state'}
                                handleButtonClick={handleButtonClick}
                                isOnEditMode={editMode && isDeltekAdminOrAuthor}
                            />
                            <ListingHeaderPopover
                                data-testid="stateEditmodeList"
                                popoverContents={
                                    <ListingStatusVariant
                                        onChange={handleStateChange}
                                        hideDeprecated={!isDeprecated}
                                    />
                                }
                                sectionLabel={'State'}
                                isPopoverOpen={popoverVariant === 'state'}
                                popoverAnchorEl={popoverAnchorEl}
                                handlePopoverClose={handlePopoverClose}
                            />
                        </GridContainer>
                    </GridItem>

                    <GridItem wrap="nowrap">
                        <span className={classes.headerLabel}>STATUS</span>
                        <GridContainer item data-testid="listingHeaderStatus">
                            <div
                                className={clsx(
                                    isDeltekPublishingAuthor &&
                                        classes.editPageButtonContainer
                                )}
                            >
                                <ListingHeaderButton
                                    buttonKey={'status'}
                                    minWidth={100}
                                    buttonLabel={
                                        isActive ? 'Active' : 'Inactive'
                                    }
                                    isPopoverOpen={popoverVariant === 'status'}
                                    handleButtonClick={handleButtonClick}
                                    isOnEditMode={
                                        (editMode ||
                                            isDeltekPublishingAuthor) &&
                                        isDeltekAdminOrAuthor
                                    }
                                />
                            </div>
                            <ListingHeaderPopover
                                popoverContents={
                                    <ListingStateVariant
                                        onChange={handleStatusChange}
                                    />
                                }
                                sectionLabel={'Status'}
                                isPopoverOpen={popoverVariant === 'status'}
                                popoverAnchorEl={popoverAnchorEl}
                                handlePopoverClose={handlePopoverClose}
                            />
                        </GridContainer>
                    </GridItem>
                    <GridItem wrap="nowrap">
                        <span className={classes.headerLabel}>
                            ASSIGN DELTEK
                        </span>
                        <>
                            <ListingDetailsUserAssignment
                                isDeltekAdmin={isDeltekAdminOrAuthor}
                                isPopoverOpen={popoverVariant === 'state'}
                                isOnEditMode={editMode}
                                poupHeaderText={'ASSIGN BPM'}
                                assignedDeltekLabel={
                                    assignedDeltekLabel ?? 'None'
                                }
                                handleDeltekUsersChange={
                                    handleDeltekUsersChange
                                }
                                buttonVariant="Deltek"
                                productInfo={productInfo}
                                onCancelClick={onCancelClick}
                                users={allDeltekUsers}
                                canEdit={canEdit}
                            />
                        </>
                    </GridItem>

                    <GridItem wrap="nowrap">
                        <span className={classes.headerLabel}>ASSIGN BPM</span>
                        <>
                            <ListingDetailsUserAssignment
                                isDeltekAdmin={isDeltekAdmin}
                                isPopoverOpen={popoverVariant === 'state'}
                                isOnEditMode={editMode}
                                poupHeaderText={'ASSIGN BPM'}
                                assignedBPMLabel={assignedBPMLabel ?? 'None'}
                                handleBPMUsersChange={handleBPMUsersChange}
                                buttonVariant="BPM"
                                productInfo={productInfo}
                                onCancelClick={onCancelClick}
                                users={allBPMUsers}
                                canEdit={canEdit}
                            />
                        </>
                    </GridItem>
                    <PDNButton
                        disabled={!!isAddProductActive}
                        isOnEditMode={editMode}
                        listingLevel={listingLevel}
                    />
                </GridContainer>
            </GridItem>
            <GridContainer style={{ flexGrow: 2 }} item md justify="flex-end">
                {canEdit && !isDeltekPublishingAuthor && (
                    <>
                        {editMode ? (
                            <GridContainer
                                item
                                direction="row"
                                justify={downMD ? undefined : 'flex-end'}
                            >
                                <div>
                                    <div>
                                        <span className={classes.headerLabel}>
                                            EDIT
                                        </span>
                                    </div>
                                    <div style={{ display: 'flex' }}>
                                        <div
                                            className={clsx(
                                                classes.actionButtonContainer,
                                                classes.cancelButtonContainer,
                                                classes.headerButtonSpacing
                                            )}
                                        >
                                            <ActionButton
                                                data-testid="listingHeaderCancelButton"
                                                onClick={onCancelClick}
                                                color="secondary"
                                                loading={
                                                    saveProductListingDetailLoading
                                                }
                                            >
                                                Cancel
                                            </ActionButton>
                                        </div>
                                        <div
                                            className={clsx(
                                                classes.actionButtonContainer,
                                                (submit &&
                                                    classes.headerButtonSpacing) ||
                                                    ''
                                            )}
                                        >
                                            <ActionButton
                                                data-testid="listingHeaderSaveButton"
                                                onClick={onSaveClick}
                                                loading={
                                                    saveProductListingDetailLoading
                                                }
                                                disabled={disableSave}
                                            >
                                                Save
                                            </ActionButton>
                                        </div>
                                        {submit && (
                                            <div
                                                className={clsx(
                                                    classes.actionButtonContainer
                                                )}
                                            >
                                                <ActionButton
                                                    data-testid="listingHeaderSubmitButton"
                                                    onClick={onPublishClick}
                                                    loading={
                                                        saveProductListingDetailLoading
                                                    }
                                                    disabled={disableSave}
                                                >
                                                    Submit
                                                </ActionButton>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </GridContainer>
                        ) : (
                            <GridContainer
                                item
                                direction="row"
                                justify={downMD ? undefined : 'flex-end'}
                                className={clsx(
                                    add &&
                                        isDeltekAdmin &&
                                        classes.optionalButtonContainer
                                )}
                                data-testid="optionalButtons"
                            >
                                {add && (
                                    <div
                                        className={classes.headerButtonSpacing}
                                    >
                                        <div>
                                            <span
                                                className={classes.headerLabel}
                                            >
                                                EDIT
                                            </span>
                                        </div>
                                        <div
                                            className={clsx(
                                                classes.actionButtonContainer,
                                                classes.addProductButtonContainer
                                            )}
                                        >
                                            <ActionButton
                                                data-testid="listingHeaderAddProductListingButton"
                                                onClick={onAddProductClick}
                                            >
                                                Add Product
                                            </ActionButton>
                                        </div>
                                    </div>
                                )}
                                {isDeltekAdmin && !isDeprecated && (
                                    <>
                                        <div
                                            className={
                                                classes.headerButtonSpacing
                                            }
                                        >
                                            <div
                                                className={clsx(
                                                    classes.actionButtonContainer,
                                                    classes.editPageButtonContainer,
                                                    classes.mfgUpdateButton,
                                                    classes.dangerButtons
                                                )}
                                                data-testid="editButtonHolder"
                                            >
                                                <ActionButton
                                                    data-testid="listingHeaderMoveCopyButton"
                                                    onClick={
                                                        handleCopyModalOpen
                                                    }
                                                >
                                                    Move/Copy
                                                </ActionButton>
                                            </div>
                                        </div>
                                        <div
                                            className={
                                                classes.headerButtonSpacing
                                            }
                                        >
                                            <div
                                                className={clsx(
                                                    classes.actionButtonContainer,
                                                    classes.editPageButtonContainer,
                                                    classes.mfgUpdateButton,
                                                    classes.dangerButtons
                                                )}
                                                data-testid="editButtonHolder"
                                            >
                                                <ActionButton
                                                    data-testid="listingHeaderDeleteButton"
                                                    onClick={handleDelete}
                                                >
                                                    {loadingDeleteSearches ? (
                                                        <CircularProgress />
                                                    ) : (
                                                        'Delete'
                                                    )}
                                                </ActionButton>
                                            </div>
                                        </div>
                                    </>
                                )}

                                {(update || isDeprecated) && (
                                    <div
                                        className={classes.headerButtonSpacing}
                                    >
                                        {!add && (
                                            <div>
                                                <span
                                                    className={
                                                        classes.headerLabel
                                                    }
                                                >
                                                    EDIT
                                                </span>
                                            </div>
                                        )}
                                        <div
                                            className={clsx(
                                                classes.actionButtonContainer,
                                                classes.editPageButtonContainer,
                                                add && classes.mfgUpdateButton
                                            )}
                                            data-testid="editButtonHolder"
                                        >
                                            <ActionButton
                                                data-testid="listingHeaderEditButton"
                                                onClick={onEditClick}
                                            >
                                                Update
                                            </ActionButton>
                                        </div>
                                    </div>
                                )}
                            </GridContainer>
                        )}
                    </>
                )}
            </GridContainer>
            <ConfirmationDialog
                openDialog={copyOrMoveModalOpen}
                handleDialogClose={handleCopyModalClose}
                dialogActionSuccessCallback={() => handleCopyOrMove('Copy')}
                dialogActionCallback={() => handleCopyOrMove('Move')}
                contentNode={confirmationDialogContent}
                removeHeaderBorder
                disabled={
                    !productListingDetailForm.sectionNames?.value ||
                    !productListingDetailForm.productType?.value
                }
                loading={loadingCopyProject || loadingMoveProject}
                auditDetails={{
                    sourceProductCount: sourceProductCount,
                    matchPropertiesCount: matchPropertiesCount,
                }}
                size="small"
            />
            <AddNotesModal
                isEditMode={!!editMode}
                open={!!isPDNModalOpen}
                onSave={handleSavePDN}
                onCancel={handleCancelCreatePDN}
                noteType={
                    typeAction[
                        tempPDNStore.actionType as keyof typeof typeAction
                    ] as NoteType
                }
                action={
                    tempPDNStore.actionType as keyof typeof noteReasonLabels
                }
                resetAction={
                    ['DELETE', 'STATUSCHANGE', 'LISTINGTYPECHANGE'].includes(
                        tempPDNStore.actionType
                    )
                        ? handleResetAddNote
                        : undefined
                }
            />
        </GridContainer>
    )
}
